import React, { useState } from "react";
import { 
  CheckCircle2, 
  Mail, 
  Check, 
  X, 
  ShieldCheck,
  AlertCircle,
  AtSign,
  ArrowRight
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { storageService } from "../service/storage";

export default function ChangeEmailForm({ 
  formData, 
  handleChange, 
  handleSubmit, 
  isLoading, 
  error, 
  success,
  shake 
}) {
  const {setChangeEmail,setChangeEmailMFA,changeEMailContext,changeEmailVar,changeEmailMFAVar} = useAuth();
  const { currentEmail, newEmail, confirmEmail, password } = formData;
  
  // Validaciones de email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidNewEmail = emailRegex.test(newEmail);
  const isValidConfirmEmail = emailRegex.test(confirmEmail);
  const emailsMatch = newEmail && confirmEmail && newEmail === confirmEmail;
  const emailsMismatch = confirmEmail && newEmail !== confirmEmail;
  const isCurrentEmailValid = emailRegex.test(currentEmail);
  const isDifferentEmail = newEmail && currentEmail && newEmail.toLowerCase() !== currentEmail.toLowerCase();
  
  const strength = [
    isCurrentEmailValid,
    isValidNewEmail,
    isDifferentEmail,
    emailsMatch,
    password && password.length > 0
  ].filter(Boolean).length;

  const getStatusLabel = () => {
    if (strength <= 2) return { text: 'Incompleto', class: 'weak' };
    if (strength <= 4) return { text: 'En progreso', class: 'medium' };
    return { text: 'Listo', class: 'strong' };
  };

  const statusInfo = getStatusLabel();

  const changeEmailFunc = async () => {
    try {
      const response = await changeEMailContext({
        email: newEmail
      });

      // comprobar respuesta
      const sessionId = response?.data?.sessionId;

      if (!sessionId) {
        throw new Error("No se recibió sessionId del servidor");
      }

      // guardar en localStorage (o tu storageService)
      localStorage.setItem("sessionId", sessionId);
      // o mejor:
      // storageService.setSession(sessionId);

      setChangeEmail(true);
      setChangeEmailMFA(true);

      console.log("CHANGE EMAIL OK", sessionId);

    } catch (error) {
      console.error("Error en change email:", error);
    }
  };

  return (
    <>
      {/* Header compacto */}
      <div className="logo-wrapper-login" style={{ marginBottom: '0.5rem' }}>
        <div className="logo-container">
          <div className="security-icon-wrapper">
            {success ? (
              <CheckCircle2 size={36} className="security-icon success" />
            ) : (
              <ShieldCheck size={36} className="security-icon" />
            )}
          </div>
        </div>
      </div>

      <div className="brand-header" style={{ marginBottom: '1rem' }}>
        <h1 style={{ fontSize: '1.25rem', marginBottom: '0.25rem' }}>
          Actualizar <span className="highlight">Correo</span>
        </h1>
        <p className="subtitle" style={{ fontSize: '0.8rem', margin: 0 }}>
          Actualiza tu dirección de correo
        </p>
      </div>

      {/* Alertas compactas */}
      {error && (
        <div className="error-message modern-error" style={{ padding: '0.5rem 0.75rem', marginBottom: '0.75rem', fontSize: '0.85rem' }}>
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="success-message modern-success" style={{ padding: '0.5rem 0.75rem', marginBottom: '0.75rem', fontSize: '0.85rem' }}>
          <CheckCircle2 size={16} />
          <span>{success}</span>
        </div>
      )}

      <form 
        className={`login-form ${shake ? 'shake-animation' : ''}`} 
        onSubmit={handleSubmit}
        style={{ gap: '0.75rem' }}
      >
        {/* Campo Correo Actual - Compacto */}
        <div className="input-field email-field-enhanced" style={{ marginBottom: '0' }}>
          <label htmlFor="currentEmail" style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>Correo Actual</label>
          <div className="input-wrapper" style={{ position: 'relative' }}>
            <AtSign size={16} className="input-icon" style={{ left: '0.75rem' }} />
            <input
              type="email"
              id="currentEmail"
              name="currentEmail"
              value={currentEmail || ''}
              onChange={handleChange}
              placeholder="tu@email.com"
              className="modern-input"
              disabled={isLoading}
              style={{ padding: '0.6rem 0.75rem 0.6rem 2.25rem', fontSize: '0.9rem' }}
            />
            {currentEmail && (
              <div style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)' }}>
                {isCurrentEmailValid ? (
                  <Check size={14} strokeWidth={3} style={{ color: '#10b981' }} />
                ) : (
                  <X size={14} strokeWidth={3} style={{ color: '#ef4444' }} />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Campo Nuevo Correo y Requisitos en línea */}
        <div className="input-field email-field-enhanced" style={{ marginBottom: '0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
            <label htmlFor="newEmail" style={{ fontSize: '0.8rem' }}>Nuevo Correo</label>
            {newEmail && (
              <span className={`strength-label ${statusInfo.class}`} style={{ fontSize: '0.7rem' }}>
                {statusInfo.text}
              </span>
            )}
          </div>
          <div className="input-wrapper" style={{ position: 'relative' }}>
            <Mail size={16} className="input-icon" style={{ left: '0.75rem' }} />
            <input
              type="email"
              id="newEmail"
              name="newEmail"
              value={newEmail || ''}
              onChange={handleChange}
              placeholder="nuevo@email.com"
              className="modern-input"
              disabled={isLoading}
              style={{ padding: '0.6rem 0.75rem 0.6rem 2.25rem', fontSize: '0.9rem' }}
            />
          </div>
          
          {/* Requisitos en horizontal */}
          {newEmail && (
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.375rem', flexWrap: 'wrap' }}>
              <RequirementItem met={isValidNewEmail} label="Formato válido" small />
              <RequirementItem met={isDifferentEmail} label="Diferente al actual" small />
              <RequirementItem met={emailsMatch} label="Coincide" small />
            </div>
          )}
        </div>

        {/* Campo Confirmar - Compacto con indicador inline */}
        <div className="input-field email-field-enhanced" style={{ marginBottom: '0' }}>
          <label htmlFor="confirmEmail" style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>
            Confirmar Correo
            {confirmEmail && emailsMatch && (
              <Check size={12} style={{ color: '#10b981', marginLeft: '0.25rem', display: 'inline' }} />
            )}
          </label>
          <div className="input-wrapper" style={{ position: 'relative' }}>
            <Check size={16} className="input-icon" style={{ left: '0.75rem' }} />
            <input
              type="email"
              id="confirmEmail"
              name="confirmEmail"
              value={confirmEmail || ''}
              onChange={handleChange}
              placeholder="Repetir email"
              className="modern-input"
              disabled={isLoading}
              style={{ 
                padding: '0.6rem 0.75rem 0.6rem 2.25rem', 
                fontSize: '0.9rem',
                borderColor: confirmEmail ? (emailsMatch ? '#10b981' : '#ef4444') : undefined
              }}
            />
          </div>
          {confirmEmail && emailsMismatch && (
            <span style={{ fontSize: '0.75rem', color: '#ef4444', marginTop: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <AlertCircle size={10} />
              No coinciden
            </span>
          )}
        </div>

        <button
          type="submit"
          className={`submit-btn ${isLoading ? "loading" : ""}`}
          disabled={isLoading || !emailsMatch || !isValidNewEmail || !isDifferentEmail}
          onClick={() => {
            changeEmailFunc();
          }}
          style={{ padding: '0.75rem', marginTop: '0.25rem' }}
        >
          {isLoading ? (
            <span className="loader" />
          ) : (
            <>
              <Mail size={16} style={{ marginRight: '6px' }} />
              Actualizar
              <ArrowRight size={14} style={{ marginLeft: '6px' }} />
            </>
          )}
        </button>
      </form>

      <div className="form-footer" style={{ marginTop: '0.75rem' }}>
        <button 
          type="button"
          className="text-link"
          onClick={() => setChangeEmail(false)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'inherit',
            padding: 0,
            fontSize: '0.85rem'
          }}
        >
          ← Volver al inicio
        </button>
      </div>
    </>
  );
}

// Componente auxiliar modificado para ser más pequeño
function RequirementItem({ met, label, small }) {
  if (small) {
    return (
      <div className={`requirement-item ${met ? 'met' : ''}`} style={{ fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
        {met ? (
          <Check size={10} className="requirement-icon" />
        ) : (
          <div className="requirement-dot" style={{ width: '6px', height: '6px' }} />
        )}
        <span style={{ color: met ? '#10b981' : '#9ca3af' }}>{label}</span>
      </div>
    );
  }
  
  return (
    <div className={`requirement-item ${met ? 'met' : ''}`}>
      {met ? (
        <Check size={12} className="requirement-icon" />
      ) : (
        <div className="requirement-dot" />
      )}
      <span>{label}</span>
    </div>
  );
}